import { useLayoutEffect, useRef } from 'react';
import { useHomeChatActions } from '../../context/HomeChatContext';

/**
 * Injects context into the chat from the current page.
 * Each page that needs to provide location/entity/scope calls this hook.
 * The chat is blind and agnostic — it only receives what pages inject.
 *
 * @param {Object | null} entry - { key, scope?, location?, ref_entity?, ref_entity_uuid?, ... } or null to clear
 * @example
 * // UCP contact page
 * useChatContextInjector(contactId ? {
 *   key: 'ucp',
 *   scope: 'ucp',
 *   location: { page: 'ucp', section: 'overview' },
 *   ref_entity: 'CONTACT',
 *   ref_entity_uuid: contactId,
 * } : null);
 */
export const useChatContextInjector = (entry) => {
  const { pushContext, removeContext, syncContextEntry } = useHomeChatActions();
  const prevKeyRef = useRef(null);

  // Sync ref on every committed render (not during interrupted renders in concurrent mode)
  useLayoutEffect(() => {
    syncContextEntry(entry);
  });

  useLayoutEffect(() => {
    const prevKey = prevKeyRef.current;
    prevKeyRef.current = entry?.key ?? null;

    if (prevKey && prevKey !== entry?.key) {
      removeContext(prevKey);
    }

    if (entry && entry.key) {
      pushContext(entry);
      return () => {
        removeContext(entry.key);
        prevKeyRef.current = null;
      };
    }
  }, [entry?.key, entry, pushContext, removeContext]);
};
